import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  ChevronRight,
  Plus,
  Check,
  Package,
  Layers,
  Sparkles,
  ArrowUpDown,
} from 'lucide-react'
import { api } from '@/lib/api'
import { formatPrice, localizedName } from '@/lib/format'
import { useT } from '@/lib/i18n'
import { useAppStore } from '@/lib/store'
import { usePageTitle } from '@/lib/usePageTitle'
import { haptic } from '@/lib/telegram'
import { Markdown } from '@/components/Markdown'
import type { BrandDetail, ProductSummary } from '@/shared/types'

type SortOption = 'popular' | 'price_asc' | 'price_desc' | 'newest'

export function BrandPage() {
  const { brandSlug } = useParams({ strict: false }) as { brandSlug?: string }
  const T = useT('brands')
  const tNav = useT('nav')
  const lang = useAppStore((s) => s.lang)
  const setCartCount = useAppStore((s) => s.setCartCount)
  const qc = useQueryClient()

  const [selectedCategory, setSelectedCategory] = useState<string>('ALL')
  const [selectedGender, setSelectedGender] = useState<string>('ALL')
  const [sortBy, setSortBy] = useState<SortOption>('popular')
  const [addedItemIds, setAddedItemIds] = useState<Set<number>>(new Set())
  const [isDescExpanded, setIsDescExpanded] = useState(false)

  const { data: brand, isLoading, isError } = useQuery<BrandDetail>({
    queryKey: ['brand', brandSlug],
    queryFn: () => api.get<BrandDetail>(`/brands/${brandSlug}`),
    enabled: Boolean(brandSlug),
    staleTime: 300_000,
  })

  usePageTitle(brand ? `${brand.name} — ${T.title}` : T.title)

  const brandDesc = useMemo(() => {
    if (!brand) return ''
    if (lang === 'en') return brand.description_en || brand.description || ''
    if (lang === 'ru') return brand.description_ru || brand.description || ''
    return brand.description_uk || brand.description || ''
  }, [brand, lang])

  const addItemMutation = useMutation({
    mutationFn: async (productId: number) => {
      await api.post('/cart/add', { product_id: productId, qty: 1 })
      return productId
    },
    onSuccess: async (productId) => {
      haptic('success')
      setAddedItemIds((prev) => new Set(prev).add(productId))
      try {
        const cart = await api.get<{ count: number }>('/cart')
        if (typeof cart?.count === 'number') {
          setCartCount(cart.count)
        }
        void qc.invalidateQueries({ queryKey: ['cart'] })
      } catch {
        // ignore
      }
      setTimeout(() => {
        setAddedItemIds((prev) => {
          const next = new Set(prev)
          next.delete(productId)
          return next
        })
      }, 2000)
    },
    onError: () => {
      haptic('error')
    },
  })

  function handleQuickAdd(e: React.MouseEvent, product: ProductSummary) {
    e.preventDefault()
    e.stopPropagation()
    addItemMutation.mutate(product.id)
  }

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    if (!brand?.products) return []

    let list = [...brand.products]

    // Category filter
    if (selectedCategory !== 'ALL') {
      list = list.filter((p) => {
        const catName = localizedName(lang, p.category_name_uk || '', p.category_name_en || '')
        return catName === selectedCategory || p.category_name_uk === selectedCategory
      })
    }

    // Gender filter (if user picked one)
    if (selectedGender !== 'ALL') {
      const gNorm = selectedGender.toLowerCase()
      list = list.filter((p) => {
        const pName = (p.name_uk + ' ' + p.name_en).toLowerCase()
        const pCat = (p.category_name_uk || '').toLowerCase()
        return pName.includes(gNorm) || pCat.includes(gNorm)
      })
    }

    // Sort
    if (sortBy === 'price_asc') {
      list.sort((a, b) => a.price_uah - b.price_uah)
    } else if (sortBy === 'price_desc') {
      list.sort((a, b) => b.price_uah - a.price_uah)
    } else if (sortBy === 'newest') {
      list.sort((a, b) => b.id - a.id)
    }

    return list
  }, [brand, selectedCategory, selectedGender, sortBy, lang])

  // Limit preview to maximum 10 products
  const displayProducts = useMemo(() => {
    return filteredProducts.slice(0, 10)
  }, [filteredProducts])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#1a202c] p-4 text-[#f8f3ea]">
        <div className="mx-auto max-w-5xl animate-pulse space-y-4">
          <div className="h-6 w-32 rounded-lg bg-[#28303c]" />
          <div className="h-44 rounded-2xl bg-[#28303c]" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3.5">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="h-64 rounded-2xl bg-[#28303c]" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (isError || !brand) {
    return (
      <div className="min-h-screen bg-[#1a202c] px-4 py-12 text-center text-[#f8f3ea]">
        <div className="mx-auto max-w-md rounded-2xl border border-[#3a4352] bg-[#242c38] p-8">
          <Layers className="mx-auto h-12 w-12 text-slate-500 mb-3" />
          <h2 className="text-lg font-bold text-[#f8f3ea]">{T.noProductsFound}</h2>
          <Link
            to="/brands"
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-[#c9a24b] px-4 py-2 text-xs font-bold text-[#1a202c] hover:bg-[#d8b25c] transition"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {T.backToBrands}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen pb-16 bg-[#1a202c] text-[#f8f3ea]">
      {/* BREADCRUMBS & TOP NAV */}
      <div className="border-b border-[#3a4352]/60 bg-[#1f2631] px-4 py-2.5 sm:px-6">
        <div className="mx-auto flex max-w-5xl items-center gap-1.5 text-xs text-slate-400">
          <Link to="/" className="hover:text-slate-200 transition">
            {tNav.home}
          </Link>
          <ChevronRight className="h-3.5 w-3.5 text-slate-600" />
          <Link to="/brands" className="hover:text-slate-200 transition">
            {T.title}
          </Link>
          <ChevronRight className="h-3.5 w-3.5 text-slate-600" />
          <span className="font-semibold text-[#c9a24b] truncate">{brand.name}</span>
        </div>
      </div>

      {/* BRAND HERO BANNER */}
      <section className="border-b border-[#3a4352] bg-gradient-to-b from-[#242c38] to-[#1a202c] px-4 py-6 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 text-center sm:text-left">
            {/* BRAND LOGO */}
            <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl bg-white p-3 shadow-lg border border-[#3a4352] flex items-center justify-center">
              {brand.logo_url ? (
                <img
                  src={brand.logo_url}
                  alt={brand.name}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <span className="font-serif text-xl font-bold tracking-wider text-slate-800 uppercase">
                  {brand.name}
                </span>
              )}
            </div>

            {/* BRAND HEADER INFO */}
            <div className="flex-1">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-[#c9a24b]/40 bg-[#c9a24b]/10 px-3 py-0.5 text-xs font-semibold text-[#c9a24b] uppercase tracking-wider mb-1.5">
                <Sparkles className="h-3 w-3" />
                <span>{T.officialBrand}</span>
              </div>

              <h1 className="font-serif text-2xl sm:text-3xl font-bold tracking-wide text-[#f8f3ea]">
                {brand.name}
              </h1>

              {brand.categories && brand.categories.length > 0 && (
                <p className="mt-1 text-xs text-slate-400">
                  {brand.categories.join(' • ')}
                </p>
              )}

              {/* STATS BADGES */}
              <div className="mt-3.5 flex flex-wrap items-center justify-center sm:justify-start gap-2 text-xs">
                <span className="rounded-xl border border-[#3a4352] bg-[#28303c] px-3 py-1 text-slate-300 font-medium">
                  {T.productCount(brand.product_count)}
                </span>
                {brand.min_price_uah > 0 && (
                  <span className="rounded-xl border border-[#3a4352] bg-[#28303c] px-3 py-1 font-semibold text-[#c9a24b]">
                    {T.priceFrom} {formatPrice(brand.min_price_uah)}
                  </span>
                )}
              </div>

              {/* BRAND DESCRIPTION */}
              {brandDesc && (
                <div className="mt-4 rounded-xl border border-[#3a4352]/70 bg-[#1f2631]/80 p-3.5 text-xs text-slate-300 backdrop-blur-xs">
                  <div className={`leading-relaxed ${!isDescExpanded && brandDesc.length > 280 ? 'line-clamp-3' : ''}`}>
                    <Markdown content={brandDesc} className="space-y-2 leading-relaxed" />
                  </div>
                  {brandDesc.length > 280 && (
                    <button
                      type="button"
                      onClick={() => setIsDescExpanded((v) => !v)}
                      className="mt-2 font-semibold text-[#c9a24b] hover:underline"
                    >
                      {isDescExpanded ? T.showLess : T.readMore}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FILTERS & SORTING TOOLBAR */}
      <section className="sticky top-0 z-10 border-b border-[#3a4352]/80 bg-[#1a202c]/95 px-4 py-3 backdrop-blur-md sm:px-6">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
          {/* CATEGORY & GENDER FILTER CHIPS */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
            <button
              type="button"
              onClick={() => {
                haptic('light')
                setSelectedCategory('ALL')
                setSelectedGender('ALL')
              }}
              className={`shrink-0 rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
                selectedCategory === 'ALL' && selectedGender === 'ALL'
                  ? 'bg-[#c9a24b] text-[#1a202c] font-bold shadow-xs'
                  : 'border border-[#3a4352] bg-[#242c38] text-slate-300 hover:text-white'
              }`}
            >
              {T.allCategories}
            </button>
            {brand.categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => {
                  haptic('light')
                  setSelectedCategory(cat)
                }}
                className={`shrink-0 rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
                  selectedCategory === cat
                    ? 'bg-[#c9a24b] text-[#1a202c] font-bold shadow-xs'
                    : 'border border-[#3a4352] bg-[#242c38] text-slate-300 hover:text-white'
                }`}
              >
                {cat}
              </button>
            ))}

            {brand.genders && brand.genders.map((gender) => (
              <button
                key={gender}
                type="button"
                onClick={() => {
                  haptic('light')
                  setSelectedGender(selectedGender === gender ? 'ALL' : gender)
                }}
                className={`shrink-0 rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
                  selectedGender === gender
                    ? 'bg-[#c9a24b] text-[#1a202c] font-bold shadow-xs'
                    : 'border border-[#3a4352] bg-[#242c38] text-slate-300 hover:text-white'
                }`}
              >
                {gender}
              </button>
            ))}
          </div>

          {/* SORT SELECTOR */}
          <div className="flex items-center gap-2 shrink-0">
            <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="rounded-xl border border-[#3a4352] bg-[#242c38] px-3 py-1.5 text-xs font-medium text-[#f8f3ea] focus:border-[#c9a24b] focus:outline-none transition cursor-pointer"
            >
              <option value="popular">{T.sortPopular}</option>
              <option value="price_asc">{T.sortPriceAsc}</option>
              <option value="price_desc">{T.sortPriceDesc}</option>
              <option value="newest">{T.sortNewest}</option>
            </select>
          </div>
        </div>
      </section>

      {/* PRODUCTS GRID */}
      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        {filteredProducts.length === 0 ? (
          <div className="py-16 text-center">
            <Package className="mx-auto h-12 w-12 text-slate-500 mb-2" />
            <p className="text-sm font-semibold text-slate-300">{T.noProductsFound}</p>
            {(selectedCategory !== 'ALL' || selectedGender !== 'ALL') && (
              <button
                type="button"
                onClick={() => {
                  setSelectedCategory('ALL')
                  setSelectedGender('ALL')
                }}
                className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-[#3a4352] bg-[#242c38] px-3.5 py-1.5 text-xs font-medium text-[#c9a24b]"
              >
                {T.allCategories}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3.5">
              {displayProducts.map((product) => {
                const productName = localizedName(lang, product.name_uk, product.name_en)
                const isAdded = addedItemIds.has(product.id)
                const productSlug = product.slug || String(product.id)

                return (
                  <div
                    key={product.id}
                    className="group flex flex-col justify-between overflow-hidden rounded-2xl border border-[#3a4352] bg-[#242c38] p-3 transition-all duration-300 hover:border-[#c9a24b] hover:bg-[#28303c] hover:shadow-lg hover:shadow-[#c9a24b]/5"
                  >
                    {/* PRODUCT IMAGE LINK */}
                    <Link
                      to="/catalog/$productSlug"
                      params={{ productSlug }}
                      onClick={() => haptic('light')}
                      className="block relative aspect-square w-full overflow-hidden rounded-xl bg-[#1a202c] border border-[#3a4352]/50"
                    >
                      {product.photo_url ? (
                        <img
                          src={product.photo_url}
                          alt={productName}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-3xl">
                          🧴
                        </div>
                      )}

                      {!product.in_stock && !product.is_made_to_order && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-2xs">
                          <span className="rounded-lg bg-rose-500/80 px-2 py-1 text-[10px] font-bold text-white uppercase">
                            {T.outOfStock}
                          </span>
                        </div>
                      )}
                    </Link>

                    {/* PRODUCT INFO */}
                    <div className="mt-3 flex flex-1 flex-col justify-between">
                      <div>
                        {product.category_name_uk && (
                          <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400 line-clamp-1">
                            {localizedName(lang, product.category_name_uk, product.category_name_en || '')}
                          </p>
                        )}
                        <Link
                          to="/catalog/$productSlug"
                          params={{ productSlug }}
                          onClick={() => haptic('light')}
                          className="mt-0.5 block text-xs sm:text-sm font-semibold text-[#f8f3ea] group-hover:text-[#c9a24b] transition-colors line-clamp-2"
                        >
                          {productName}
                        </Link>
                      </div>

                      {/* PRICE & ADD BUTTON */}
                      <div className="mt-3 flex items-center justify-between gap-1.5 pt-2 border-t border-[#3a4352]/60">
                        <div>
                          <p className="text-xs sm:text-sm font-bold text-[#c9a24b]">
                            {formatPrice(product.price_uah)}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={(e) => handleQuickAdd(e, product)}
                          disabled={!product.in_stock && !product.is_made_to_order}
                          className={`flex h-8 w-8 items-center justify-center rounded-xl transition shadow-xs ${
                            isAdded
                              ? 'bg-emerald-500 text-white scale-110'
                              : 'bg-[#c9a24b] text-[#1a202c] hover:bg-[#d8b25c] active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed'
                          }`}
                          title={isAdded ? 'Added' : 'Add to cart'}
                        >
                          {isAdded ? (
                            <Check className="h-4 w-4 stroke-[3]" />
                          ) : (
                            <Plus className="h-4 w-4 stroke-[2.5]" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* VIEW ALL IN CATALOG BUTTON */}
            {brand.products.length > 0 && (
              <div className="mt-8 text-center">
                <Link
                  to="/catalog"
                  search={{ attr: 'brand', value: brand.name }}
                  className="inline-flex items-center gap-2 rounded-2xl border border-[#c9a24b] bg-[#c9a24b]/10 px-6 py-3 text-sm font-bold text-[#c9a24b] hover:bg-[#c9a24b] hover:text-[#1a202c] transition shadow-md"
                >
                  <span>{T.viewAllInCatalog(brand.product_count)}</span>
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}
